import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { money2, errMsg } from "./payrollHelpers";

export default function CommissionSplitModal({ employeeId, employeeName, job, systemAmount, otherTechs, onClose, onSubmit }) {
  const [otherTechId, setOtherTechId] = useState(otherTechs?.[0]?.id || "");
  const [commissionableAmount, setCommissionableAmount] = useState(job.invoiceAmount);
  const [pct, setPct] = useState(job.commissionPct);
  const [shareA, setShareA] = useState(60);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const pool = Math.round(commissionableAmount * (pct / 100) * 100) / 100;
  const shareB = 100 - shareA;
  const amountA = Math.round(pool * (shareA / 100) * 100) / 100;
  const amountB = Math.round(pool * (shareB / 100) * 100) / 100;
  const otherTech = otherTechs.find((t) => t.id === otherTechId);

  const submit = async (e) => {
    e.preventDefault();
    if (!otherTechId) return toast.error("Select the other technician");
    if (!reason.trim()) return toast.error("A reason is required");
    setBusy(true);
    try {
      await onSubmit([
        {
          employeeId, category: "COMMISSION", jobId: job.jobId, splitTechId: otherTechId,
          originalAmountOverride: systemAmount, adjustmentAmount: Math.round((amountA - systemAmount) * 100) / 100,
          reason: reason.trim(), notes: `Split ${shareA}/${shareB} with ${otherTech?.name || otherTechId}`,
        },
        {
          employeeId: otherTechId, category: "COMMISSION", jobId: job.jobId, splitTechId: employeeId,
          originalAmountOverride: 0, adjustmentAmount: amountB,
          reason: reason.trim(), notes: `Split ${shareB}/${shareA} with ${employeeName}`,
        },
      ]);
      onClose();
    } catch (err) {
      toast.error(errMsg(err, "Failed to save commission split"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200" data-testid="commission-split-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[14px] font-semibold text-slate-900">Split Commission — {job.customer && job.customer !== "—" ? job.customer : job.jobId}</div>
          <button type="button" onClick={onClose} className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Split with</label>
            <select value={otherTechId} onChange={(e) => setOtherTechId(e.target.value)} data-testid="commission-split-tech-select" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px] bg-white">
              {(otherTechs || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1">Total Sale / Commissionable Amount</label>
              <input type="number" step="0.01" value={commissionableAmount} onChange={(e) => setCommissionableAmount(parseFloat(e.target.value) || 0)} data-testid="commission-split-amount" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1">Commission %</label>
              <input type="number" step="0.1" value={pct} onChange={(e) => setPct(parseFloat(e.target.value) || 0)} data-testid="commission-split-pct" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" />
            </div>
          </div>
          <div className="text-[12px] text-slate-500">Commission Pool: <span className="font-semibold text-slate-700">{money2(pool)}</span></div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">{employeeName} share: {shareA}%</label>
            <input type="range" min="0" max="100" step="5" value={shareA} onChange={(e) => setShareA(parseInt(e.target.value, 10))} data-testid="commission-split-slider" className="w-full" />
          </div>
          <div className="grid grid-cols-2 gap-3 text-[12.5px]">
            <div className="rounded-md border border-slate-200 p-2.5">
              <div className="font-semibold text-slate-700">{employeeName}</div>
              <div className="text-slate-500">{shareA}% share</div>
              <div className="font-bold text-emerald-700" data-testid="commission-split-amount-a">{money2(amountA)}</div>
            </div>
            <div className="rounded-md border border-slate-200 p-2.5">
              <div className="font-semibold text-slate-700">{otherTech?.name || "—"}</div>
              <div className="text-slate-500">{shareB}% share</div>
              <div className="font-bold text-emerald-700" data-testid="commission-split-amount-b">{money2(amountB)}</div>
            </div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Reason <span className="text-rose-500">*</span></label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="commission-split-reason" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" placeholder="e.g. Both technicians worked this install" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/40">
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-md border border-slate-200 text-[13px] font-semibold">Cancel</button>
          <button type="submit" disabled={busy} data-testid="commission-split-submit-btn" className="h-9 px-3 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-[13px] font-semibold">{busy ? "Saving…" : "Save Split"}</button>
        </div>
      </form>
    </div>
  );
}
